import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Html } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

import Text from '@cartwhl/ui/base/Text';

import type {
  BoneAssignmentsViewerPickMode,
  BoneAssignmentsViewerFocus,
  HoveredBoneAssignmentChain,
} from '../../BoneAssignments';
import { dispatchViewerBoneHoverName } from '../../BoneAssignments/viewerBoneHoverEvent';
import type { BoneColorPreset } from '../../../cartographerStore';
import type { Skeleton } from '../../../lib/skeleton/core';
import type { MappingResult } from '../../../lib/recognize/config';
import {
  getChainCssColor,
  getChainThreeColor,
} from '../../../presentations/chain';
import {
  getConfidenceCssColor,
  normalizeConfidence,
} from '../../../presentations/confidence';
import { BonePropertiesBadge } from '../../../presentations/BonePropertiesBadge';
import {
  getBonePropertyIndicatorState,
  getBonePropertyTypeLabels,
} from '../../../presentations/properties';

const EXCLUDED_BONE_COLOR = 0xef4444;
const BONE_COLOR_PRESET_MAP: Record<BoneColorPreset, number> = {
  white: 0xffffff,
  grey: 0x808080,
  charcoal: 0x555555,
  black: 0x000000,
};
const BONE_STROKE_WIDTH = 3;
const JOINT_RADIUS_FACTOR = 0.006;
const JOINT_RADIUS_SCALE = 1.5;
const EXCLUDED_RING_DASH_SIZE = 0.42;
const EXCLUDED_RING_GAP_SIZE = 0.36;
const EXCLUDED_RING_DASH_SCALE = 1;
const EXCLUDED_LINE_DASH_FACTOR = 0.009;
const EXCLUDED_LINE_GAP_FACTOR = 0.006;
const EXCLUDED_RING_SEGMENTS = 48;
const BONE_LINE_RENDER_ORDER = 999;
const CHAIN_LINE_RENDER_ORDER = 999.5;
const CHAIN_LINE_WIDTH = 6;
const CHAIN_LINE_OPACITY = 1;
const CHAIN_SINGLE_MARKER_RENDER_ORDER = 999.6;
const CHAIN_SINGLE_MARKER_SIZE_PX = CHAIN_LINE_WIDTH * 2;
const CHAIN_ENDPOINT_HIGHLIGHT_SCALE_MULTIPLIER = 1.08;
const LINE_PICK_RADIUS_FACTOR = 0.0075;
const CHAIN_PICK_RADIUS_FACTOR = 0.0085;
const PICK_RADIUS_MIN_FACTOR = 0.002;
const PICK_RADIUS_MAX_FACTOR = 0.04;
const CHAIN_MARKER_PICK_RADIUS_SCALE = 1.3;
const RENDER_SCALE_MIN = 0.1;
const RENDER_SCALE_MAX = 10;
const ROLE_TAG_USER_ASSIGNED_BG = 'rgba(255, 255, 255, 0.96)';
const HTML_OVERLAY_Z_INDEX_RANGE: [number, number] = [10, 0];
const BONE_PICK_INDEX_USER_DATA_KEY = 'cartographerBonePickIndex';
const CHAIN_PICK_USER_DATA_KEY = 'cartographerChainPick';

const EMPTY_MAPPING_IO_EXTRAS: Record<string, unknown> = {};

// ─── Component ──────────────────────────────────────────────────────────

const SELECTION_HALO_COLOR = new THREE.Color(1, 1, 1);
const SELECTION_HALO_OPACITY = 0.69;
const SELECTION_PULSE_DURATION_SECONDS = 0.5;
const SELECTION_PULSE_START_SCALE = 1;
const SELECTION_PULSE_PEAK_SCALE = 1.56;
const SELECTION_PULSE_SETTLE_SCALE = 1.44;
const ROLE_PICK_PREVIEW_COLOR = 0xffffff;
const BONE_PICK_PREVIEW_OPACITY = 0.28;
const BONE_PICK_PREVIEW_SCALE_MULTIPLIER = 1.22;

type SkeletonOverlayProps = {
  skeleton: Skeleton;
  mapping?: MappingResult | null;
  assignedBoneIndexSet: Set<number> | null;
  excludedBoneRefs?: string[];
  showBones?: boolean;
  showExcludedBones?: boolean;
  showUnassignedBones?: boolean;
  showChains?: boolean;
  boneColorPreset?: BoneColorPreset;
  boneRadiusScale?: number;
  chainThicknessScale?: number;
  showBoneNames?: boolean;
  showRoles?: boolean;
  forcedRoleTag?: {
    boneIndex: number;
    role: string;
    confidence: number | null;
  } | null;
  boneAssignmentsViewerFocus?: BoneAssignmentsViewerFocus | null;
  hoveredBoneAssignmentChain?: HoveredBoneAssignmentChain | null;
  boneAssignmentsViewerPickMode?: BoneAssignmentsViewerPickMode | null;
  showProperties?: boolean;
  propertiesVersion?: number;
  poseVersion?: number;
  selectedBoneIndex?: number | null;
  onSelectBone?: (index: number | null) => void;
  onSelectBoneFromPropertiesBadge?: (index: number) => void;
  selectionStopsPropagation?: boolean;
  showSelectionHighlight?: boolean;
};

type BoneRoleTag = {
  role: string;
  normalizedConfidence: number | null;
  backgroundColor: string;
  isUserAssigned: boolean;
};

type HoveredChainEndpoint = {
  key: 'start' | 'end' | 'shared';
  boneIndex: number;
  position: THREE.Vector3;
  boneName: string;
  endpointLabel: 'Start' | 'End' | 'Start / End';
};

type SegmentHitEdge = {
  start: THREE.Vector3;
  end: THREE.Vector3;
  targetBoneIndex: number;
};

type SegmentHitTarget = {
  key: string;
  midpoint: THREE.Vector3;
  quaternion: THREE.Quaternion;
  length: number;
  targetBoneIndex: number;
};

type MarkerHitTarget = {
  key: string;
  position: THREE.Vector3;
  targetBoneIndex: number;
};

function clampRenderScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(RENDER_SCALE_MAX, Math.max(RENDER_SCALE_MIN, value));
}

function buildSegmentHitTargets(
  edges: SegmentHitEdge[],
  keyPrefix: string,
): SegmentHitTarget[] {
  const worldUp = new THREE.Vector3(0, 1, 0);
  const targets: SegmentHitTarget[] = [];
  for (let i = 0; i < edges.length; i += 1) {
    const edge = edges[i];
    const direction = edge.end.clone().sub(edge.start);
    const length = direction.length();
    if (length <= 1e-5) continue;
    const midpoint = edge.start.clone().addScaledVector(direction, 0.5);
    direction.multiplyScalar(1 / length);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(
      worldUp,
      direction,
    );
    targets.push({
      key: `${keyPrefix}-${i}`,
      midpoint,
      quaternion,
      length,
      targetBoneIndex: edge.targetBoneIndex,
    });
  }
  return targets;
}

function buildUnitRingGeometry(): THREE.BufferGeometry {
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < EXCLUDED_RING_SEGMENTS; i += 1) {
    const angle = (i / EXCLUDED_RING_SEGMENTS) * Math.PI * 2;
    points.push(new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0));
  }
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.LineLoop(geometry);
  line.computeLineDistances();
  return geometry;
}

function buildCircularDotTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return null;

  context.clearRect(0, 0, size, size);
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.arc(size / 2, size / 2, size * 0.42, 0, Math.PI * 2);
  context.fill();

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function appendDashedSegmentPositions(
  positions: number[],
  start: THREE.Vector3,
  end: THREE.Vector3,
  dashLength: number,
  gapLength: number,
) {
  const delta = new THREE.Vector3().subVectors(end, start);
  const segmentLength = delta.length();
  if (segmentLength <= 0) return;

  const direction = delta.multiplyScalar(1 / segmentLength);
  const safeDashLength = Math.max(1e-4, dashLength);
  const safeGapLength = Math.max(0, gapLength);
  let distance = 0;

  while (distance < segmentLength) {
    const dashEndDistance = Math.min(distance + safeDashLength, segmentLength);
    if (dashEndDistance > distance) {
      const dashStart = start.clone().addScaledVector(direction, distance);
      const dashEnd = start.clone().addScaledVector(direction, dashEndDistance);
      positions.push(
        dashStart.x,
        dashStart.y,
        dashStart.z,
        dashEnd.x,
        dashEnd.y,
        dashEnd.z,
      );
    }
    distance += safeDashLength + safeGapLength;
  }
}

function trimParentLineToBoneRadius(
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
): { start: THREE.Vector3; end: THREE.Vector3 } | null {
  const delta = new THREE.Vector3().subVectors(end, start);
  const segmentLength = delta.length();
  if (segmentLength <= 0) return null;

  const maxTrim = Math.max(0, (segmentLength - 1e-4) * 0.5);
  const trimDistance = Math.min(Math.max(radius, 0), maxTrim);
  const direction = delta.multiplyScalar(1 / segmentLength);

  return {
    start: start.clone().addScaledVector(direction, trimDistance),
    end: end.clone().addScaledVector(direction, -trimDistance),
  };
}

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function getSelectionPulseScale(progress: number): number {
  const p = Math.max(0, Math.min(1, progress));
  if (p < 0.62) {
    return lerp(
      SELECTION_PULSE_START_SCALE,
      SELECTION_PULSE_PEAK_SCALE,
      easeInOutSine(p / 0.62),
    );
  }
  return lerp(
    SELECTION_PULSE_PEAK_SCALE,
    SELECTION_PULSE_SETTLE_SCALE,
    easeInOutSine((p - 0.62) / 0.38),
  );
}

function SelectionHalo({
  position,
  geometry,
  bonePickIndex,
  onPointerDown,
  onPointerOver,
  onPointerMove,
  onPointerOut,
  onClick,
}: {
  position: THREE.Vector3;
  geometry: THREE.SphereGeometry;
  bonePickIndex: number;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>) => void;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
}) {
  const haloRef = useRef<THREE.Mesh>(null);
  const elapsedRef = useRef(0);

  useFrame((_, delta) => {
    const halo = haloRef.current;
    if (!halo) return;
    elapsedRef.current = Math.min(
      SELECTION_PULSE_DURATION_SECONDS,
      elapsedRef.current + delta,
    );
    const progress = elapsedRef.current / SELECTION_PULSE_DURATION_SECONDS;
    const scale = getSelectionPulseScale(progress);
    halo.scale.setScalar(scale);
  });

  return (
    <mesh
      ref={haloRef}
      position={position}
      geometry={geometry}
      renderOrder={999}
      userData={{ [BONE_PICK_INDEX_USER_DATA_KEY]: bonePickIndex }}
      onPointerDown={onPointerDown}
      onPointerOver={onPointerOver}
      onPointerMove={onPointerMove}
      onPointerOut={onPointerOut}
      onClick={onClick}
    >
      <meshBasicMaterial
        color={SELECTION_HALO_COLOR}
        depthTest={false}
        transparent
        opacity={SELECTION_HALO_OPACITY}
      />
    </mesh>
  );
}

function ExcludedBoneRing({
  position,
  radius,
}: {
  position: THREE.Vector3;
  radius: number;
}) {
  const ringRef = useRef<THREE.LineLoop>(null);
  const geometry = useMemo(() => buildUnitRingGeometry(), []);

  useFrame(({ camera }) => {
    const ring = ringRef.current;
    if (!ring) return;
    ring.quaternion.copy(camera.quaternion);
  });

  return (
    <lineLoop
      ref={ringRef}
      position={position}
      geometry={geometry}
      scale={[radius, radius, 1]}
      renderOrder={1001}
    >
      <lineDashedMaterial
        color={EXCLUDED_BONE_COLOR}
        dashSize={EXCLUDED_RING_DASH_SIZE}
        gapSize={EXCLUDED_RING_GAP_SIZE}
        scale={EXCLUDED_RING_DASH_SCALE}
        linewidth={BONE_STROKE_WIDTH}
        transparent={false}
        depthTest={false}
      />
    </lineLoop>
  );
}

export default function SkeletonOverlay({
  skeleton,
  mapping,
  assignedBoneIndexSet,
  excludedBoneRefs = [],
  showBones = true,
  showExcludedBones = true,
  showUnassignedBones = true,
  showChains = true,
  boneColorPreset = 'charcoal',
  boneRadiusScale = 1,
  chainThicknessScale = 1,
  showBoneNames = false,
  showRoles = false,
  forcedRoleTag = null,
  boneAssignmentsViewerFocus = null,
  hoveredBoneAssignmentChain = null,
  boneAssignmentsViewerPickMode = null,
  showProperties = false,
  propertiesVersion = 0,
  poseVersion = 0,
  selectedBoneIndex,
  onSelectBone,
  onSelectBoneFromPropertiesBadge,
  selectionStopsPropagation = true,
  showSelectionHighlight = true,
}: SkeletonOverlayProps) {
  const baseBoneColor = BONE_COLOR_PRESET_MAP[boneColorPreset];
  const safeBoneRadiusScale = clampRenderScale(boneRadiusScale);
  const safeChainThicknessScale = clampRenderScale(chainThicknessScale);
  const effectiveChainWidthPx = CHAIN_LINE_WIDTH * safeChainThicknessScale;
  const effectiveChainMarkerSizePx =
    CHAIN_SINGLE_MARKER_SIZE_PX * safeChainThicknessScale;
  const { size } = useThree();
  const gl = useThree((state) => state.gl);
  const [hoveredBoneIndex, setHoveredBoneIndex] = useState<number | null>(null);
  const chainDotTexture = useMemo(() => buildCircularDotTexture(), []);
  const excludedBoneSet = useMemo(
    () =>
      new Set<number>(
        excludedBoneRefs
          .map((ref) => skeleton.resolveBoneRef(ref, { namespace: 'internal' }))
          .filter((idx): idx is number => idx !== null),
      ),
    [excludedBoneRefs, skeleton],
  );
  const roleTagsByBone = useMemo(() => {
    const tagsByBone = new Map<number, BoneRoleTag[]>();
    if (!mapping) return tagsByBone;

    for (const [role, boneRef] of Object.entries(mapping.roleToBone)) {
      const boneIndex = skeleton.resolveBoneRef(boneRef, {
        namespace: 'internal',
      });
      if (boneIndex === null) continue;
      const rawConfidence = mapping.roleToConfidence[role];
      const normalizedConfidence =
        rawConfidence == null ? null : normalizeConfidence(rawConfidence);
      const isUserAssigned = normalizedConfidence == null;
      const backgroundColor =
        isUserAssigned
          ? ROLE_TAG_USER_ASSIGNED_BG
          : getConfidenceCssColor(normalizedConfidence);

      const tags = tagsByBone.get(boneIndex);
      if (tags) {
        tags.push({ role, normalizedConfidence, backgroundColor, isUserAssigned });
      } else {
        tagsByBone.set(boneIndex, [
          { role, normalizedConfidence, backgroundColor, isUserAssigned },
        ]);
      }
    }

    for (const tags of tagsByBone.values()) {
      tags.sort((a, b) => {
        const confidenceDiff =
          (b.normalizedConfidence ?? -1) - (a.normalizedConfidence ?? -1);
        if (confidenceDiff !== 0) return confidenceDiff;
        return a.role.localeCompare(b.role);
      });
    }

    return tagsByBone;
  }, [mapping, skeleton]);
  const visibleRoleTagsByBone = useMemo(() => {
    const tagsByBone = new Map<number, BoneRoleTag[]>();
    const focusedViewerRoleName =
      boneAssignmentsViewerFocus?.mode === 'roles'
        ? boneAssignmentsViewerFocus.roleName
        : null;
    if (showRoles) {
      for (const [boneIndex, tags] of roleTagsByBone.entries()) {
        const nextTags = focusedViewerRoleName
          ? tags.filter((tag) => tag.role === focusedViewerRoleName)
          : tags;
        if (nextTags.length > 0) {
          tagsByBone.set(boneIndex, [...nextTags]);
        }
      }
    }
    if (forcedRoleTag) {
      if (focusedViewerRoleName && forcedRoleTag.role !== focusedViewerRoleName) {
        return tagsByBone;
      }
      const normalizedConfidence =
        forcedRoleTag.confidence == null
          ? null
          : normalizeConfidence(forcedRoleTag.confidence);
      const isUserAssigned = normalizedConfidence == null;
      const backgroundColor =
        isUserAssigned
          ? ROLE_TAG_USER_ASSIGNED_BG
          : getConfidenceCssColor(normalizedConfidence);
      const nextTags = [...(tagsByBone.get(forcedRoleTag.boneIndex) ?? [])];
      if (!nextTags.some((tag) => tag.role === forcedRoleTag.role)) {
        nextTags.push({
          role: forcedRoleTag.role,
          normalizedConfidence,
          backgroundColor,
          isUserAssigned,
        });
      }
      nextTags.sort((a, b) => {
        const confidenceDiff =
          (b.normalizedConfidence ?? -1) - (a.normalizedConfidence ?? -1);
        if (confidenceDiff !== 0) return confidenceDiff;
        return a.role.localeCompare(b.role);
      });
      tagsByBone.set(forcedRoleTag.boneIndex, nextTags);
    }
    return tagsByBone;
  }, [boneAssignmentsViewerFocus, forcedRoleTag, roleTagsByBone, showRoles]);
  const focusedChainEndpointColorByBone = useMemo(() => {
    const colorByBone = new Map<number, number>();
    if (boneAssignmentsViewerFocus?.mode !== 'chains' || !mapping) {
      return colorByBone;
    }

    const chainName = boneAssignmentsViewerFocus.chainName;
    const chainColor = getChainThreeColor(chainName);
    const chainBoneRefs = mapping.chains?.[chainName] ?? [];
    const chainResult = mapping.chainResults?.[chainName];
    const startBoneRef = chainResult?.startBone ?? chainBoneRefs[0] ?? null;
    const endBoneRef =
      chainResult?.endBone ?? chainBoneRefs[chainBoneRefs.length - 1] ?? null;
    const addEndpointBone = (boneRef: string | null) => {
      if (!boneRef) return;
      const boneIndex = skeleton.resolveBoneRef(boneRef, {
        namespace: 'internal',
      });
      if (boneIndex === null) return;
      colorByBone.set(boneIndex, chainColor);
    };

    addEndpointBone(startBoneRef);
    addEndpointBone(endBoneRef);
    return colorByBone;
  }, [boneAssignmentsViewerFocus, mapping, skeleton]);
  const hoveredChainColor = hoveredBoneAssignmentChain
    ? getChainThreeColor(hoveredBoneAssignmentChain.chainName)
    : null;
  const hoveredChainColorCss = hoveredBoneAssignmentChain
    ? getChainCssColor(hoveredBoneAssignmentChain.chainName)
    : null;
  const hoveredChainEndpoints = useMemo(() => {
    if (!hoveredBoneAssignmentChain) return [];

    const nextEndpoints: HoveredChainEndpoint[] = [];
    const pushEndpoint = (
      key: HoveredChainEndpoint['key'],
      boneIndex: number | null,
      boneName: string | null,
      endpointLabel: HoveredChainEndpoint['endpointLabel'],
    ) => {
      if (boneIndex === null || !boneName) return;
      if (boneIndex < 0 || boneIndex >= skeleton.bones.length) return;
      nextEndpoints.push({
        key,
        boneIndex,
        position: skeleton.getBonePosedWorldPosition(boneIndex),
        boneName,
        endpointLabel,
      });
    };

    const {
      startBoneIndex,
      startBoneName,
      endBoneIndex,
      endBoneName,
    } = hoveredBoneAssignmentChain;
    const hasSharedEndpoint =
      startBoneIndex !== null &&
      startBoneIndex === endBoneIndex &&
      startBoneName &&
      startBoneName === endBoneName;

    if (hasSharedEndpoint) {
      pushEndpoint('shared', startBoneIndex, startBoneName, 'Start / End');
      return nextEndpoints;
    }

    pushEndpoint('start', startBoneIndex, startBoneName, 'Start');
    pushEndpoint('end', endBoneIndex, endBoneName, 'End');
    return nextEndpoints;
  }, [hoveredBoneAssignmentChain, poseVersion, skeleton]);
  const mappingIoExtras = mapping?.ioExtras ?? EMPTY_MAPPING_IO_EXTRAS;
  const { propertyBoneIndices, propertyBadgeTooltipByBone } = useMemo(() => {
    const nextPropertyBoneIndices = new Set<number>();
    const nextPropertyBadgeTooltipByBone = new Map<number, string[]>();
    if (!showProperties) {
      return {
        propertyBoneIndices: nextPropertyBoneIndices,
        propertyBadgeTooltipByBone: nextPropertyBadgeTooltipByBone,
      };
    }

    // Inspector mutates properties in place; this version forces fresh evaluation.
    const _currentPropertiesVersion = propertiesVersion;
    for (const bone of skeleton.bones) {
      if (_currentPropertiesVersion < 0) continue;
      const propertyState = getBonePropertyIndicatorState(bone, mappingIoExtras);
      const labels = getBonePropertyTypeLabels(propertyState);
      if (labels.length === 0) continue;

      nextPropertyBoneIndices.add(bone.index);
      nextPropertyBadgeTooltipByBone.set(bone.index, labels);
    }

    return {
      propertyBoneIndices: nextPropertyBoneIndices,
      propertyBadgeTooltipByBone: nextPropertyBadgeTooltipByBone,
    };
  }, [mappingIoExtras, propertiesVersion, showProperties, skeleton]);

  const {
    lineSegments,
    excludedLineSegments,
    hasExcludedParentSegments,
    jointData,
    extent,
    bonePositionsByIndex,
    boneLineHitEdges,
  } = useMemo(() => {
    const rawSegmentsForExtent: number[] = [];
    const defaultParentEdges: SegmentHitEdge[] = [];
    const excludedParentEdges: SegmentHitEdge[] = [];
    const worldPositionsByIndex = new Map<number, THREE.Vector3>();
    const joints: {
      pos: THREE.Vector3;
      color: number;
      idx: number;
      isExcluded: boolean;
    }[] = [];

    for (const bone of skeleton.bones) {
      const isExcluded = excludedBoneSet.has(bone.index);
      const isAssigned =
        assignedBoneIndexSet === null || assignedBoneIndexSet.has(bone.index);
      const shouldRenderBone =
        (showExcludedBones || !isExcluded) &&
        (showUnassignedBones || isAssigned);
      if (!shouldRenderBone) {
        continue;
      }
      const pos = skeleton.getBonePosedWorldPosition(bone.index);
      worldPositionsByIndex.set(bone.index, pos.clone());
      const jointColor = baseBoneColor;

      joints.push({ pos, color: jointColor, idx: bone.index, isExcluded });

      // Leaf bones already terminate at this joint; extending by bone.length
      // would create a synthetic trailing segment with no real descendant.
      if (bone.children.length > 0) {
        for (const childIdx of bone.children) {
          const childPos = skeleton.getBonePosedWorldPosition(childIdx);
          const childIsExcluded = excludedBoneSet.has(childIdx);
          const childIsAssigned =
            assignedBoneIndexSet === null ||
            assignedBoneIndexSet.has(childIdx);
          const shouldRenderChild =
            (showExcludedBones || !childIsExcluded) &&
            (showUnassignedBones || childIsAssigned);
          if (!shouldRenderChild) {
            continue;
          }

          if (childIsExcluded) {
            excludedParentEdges.push({
              start: pos.clone(),
              end: childPos.clone(),
              targetBoneIndex: bone.index,
            });
          } else {
            defaultParentEdges.push({
              start: pos.clone(),
              end: childPos.clone(),
              targetBoneIndex: bone.index,
            });
          }

          rawSegmentsForExtent.push(
            pos.x,
            pos.y,
            pos.z,
            childPos.x,
            childPos.y,
            childPos.z,
          );
        }
      }
    }

    const extentGeo = new THREE.BufferGeometry();
    extentGeo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(rawSegmentsForExtent, 3),
    );
    extentGeo.computeBoundingSphere();
    const rawExtent = extentGeo.boundingSphere?.radius ?? 1;
    extentGeo.dispose();
    const ext = Number.isFinite(rawExtent) && rawExtent > 0 ? rawExtent : 1;
    const lineTrimRadius =
      ext * JOINT_RADIUS_FACTOR * JOINT_RADIUS_SCALE * safeBoneRadiusScale;

    const defaultSegments: number[] = [];
    const nextBoneLineHitEdges: SegmentHitEdge[] = [];
    for (const edge of defaultParentEdges) {
      const trimmed = trimParentLineToBoneRadius(
        edge.start,
        edge.end,
        lineTrimRadius,
      );
      if (!trimmed) continue;
      nextBoneLineHitEdges.push({
        start: trimmed.start.clone(),
        end: trimmed.end.clone(),
        targetBoneIndex: edge.targetBoneIndex,
      });
      defaultSegments.push(
        trimmed.start.x,
        trimmed.start.y,
        trimmed.start.z,
        trimmed.end.x,
        trimmed.end.y,
        trimmed.end.z,
      );
    }
    const excludedDashLength = ext * EXCLUDED_LINE_DASH_FACTOR;
    const excludedGapLength = ext * EXCLUDED_LINE_GAP_FACTOR;
    const excludedParentDashedSegments: number[] = [];
    for (const edge of excludedParentEdges) {
      const trimmed = trimParentLineToBoneRadius(
        edge.start,
        edge.end,
        lineTrimRadius,
      );
      if (!trimmed) continue;
      nextBoneLineHitEdges.push({
        start: trimmed.start.clone(),
        end: trimmed.end.clone(),
        targetBoneIndex: edge.targetBoneIndex,
      });
      appendDashedSegmentPositions(
        excludedParentDashedSegments,
        trimmed.start,
        trimmed.end,
        excludedDashLength,
        excludedGapLength,
      );
    }

    const defaultGeo = new THREE.BufferGeometry();
    defaultGeo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(defaultSegments, 3),
    );

    const excludedGeo = new THREE.BufferGeometry();
    excludedGeo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(excludedParentDashedSegments, 3),
    );

    return {
      lineSegments: defaultGeo,
      excludedLineSegments: excludedGeo,
      hasExcludedParentSegments: excludedParentDashedSegments.length > 0,
      jointData: joints,
      extent: ext,
      bonePositionsByIndex: worldPositionsByIndex,
      boneLineHitEdges: nextBoneLineHitEdges,
    };
  }, [
    assignedBoneIndexSet,
    baseBoneColor,
    excludedBoneSet,
    poseVersion,
    safeBoneRadiusScale,
    showExcludedBones,
    showUnassignedBones,
    skeleton,
  ]);

  const jointRadius =
    extent * JOINT_RADIUS_FACTOR * JOINT_RADIUS_SCALE * safeBoneRadiusScale;
  const jointGeo = useMemo(
    () => new THREE.SphereGeometry(jointRadius, 8, 6),
    [jointRadius],
  );
  const selectionJointGeo = useMemo(
    () => new THREE.SphereGeometry(jointRadius, 16, 12),
    [jointRadius],
  );
  const segmentPickGeo = useMemo(
    () => new THREE.CylinderGeometry(1, 1, 1, 8, 1, true),
    [],
  );
  const markerPickGeo = useMemo(() => new THREE.SphereGeometry(1, 10, 8), []);
  const maxPickRadius = extent * PICK_RADIUS_MAX_FACTOR;
  const boneLinePickRadius = Math.min(
    maxPickRadius,
    Math.max(
      extent * LINE_PICK_RADIUS_FACTOR * safeBoneRadiusScale,
      extent * PICK_RADIUS_MIN_FACTOR,
    ),
  );
  const chainLinePickRadius = Math.min(
    maxPickRadius,
    Math.max(
      extent * CHAIN_PICK_RADIUS_FACTOR * safeChainThicknessScale,
      boneLinePickRadius,
    ),
  );
  const chainMarkerPickRadius = Math.min(
    maxPickRadius,
    chainLinePickRadius * CHAIN_MARKER_PICK_RADIUS_SCALE,
  );
  const boneLineHitTargets = useMemo(
    () => buildSegmentHitTargets(boneLineHitEdges, 'bone-line-hit'),
    [boneLineHitEdges],
  );

  const {
    chainLineObjects,
    chainSingleDotObjects,
    chainLineHitEdges,
    chainMarkerHitTargets,
  } = useMemo(() => {
    const focusedViewerChainName =
      boneAssignmentsViewerFocus?.mode === 'chains'
        ? boneAssignmentsViewerFocus.chainName
        : null;
    if (!showChains || !mapping) {
      return {
        chainLineObjects: [],
        chainSingleDotObjects: [],
        chainLineHitEdges: [],
        chainMarkerHitTargets: [],
      };
    }

    const segmentsByColor = new Map<number, number[]>();
    const singleChainMarkerDotsByColor = new Map<number, number[]>();
    const nextChainLineHitEdges: SegmentHitEdge[] = [];
    const nextChainMarkerHitTargets: MarkerHitTarget[] = [];

    const getSegmentBucket = (color: number): number[] => {
      let positions = segmentsByColor.get(color);
      if (!positions) {
        positions = [];
        segmentsByColor.set(color, positions);
      }
      return positions;
    };

    const getSingleChainMarkerBucket = (color: number): number[] => {
      let positions = singleChainMarkerDotsByColor.get(color);
      if (!positions) {
        positions = [];
        singleChainMarkerDotsByColor.set(color, positions);
      }
      return positions;
    };

    const appendSingleChainMarkerDot = (
      color: number,
      startPos: THREE.Vector3,
    ) => {
      const markerDotPositions = getSingleChainMarkerBucket(color);
      markerDotPositions.push(startPos.x, startPos.y, startPos.z);
    };

    for (const [chainName, chainBoneRefs] of Object.entries(mapping.chains ?? {})) {
      if (focusedViewerChainName && chainName !== focusedViewerChainName) {
        continue;
      }
      const chainStartBoneRef =
        mapping.chainResults?.[chainName]?.startBone ?? chainBoneRefs[0];
      if (!chainStartBoneRef) continue;
      const firstBoneIndex = skeleton.resolveBoneRef(chainStartBoneRef, {
        namespace: 'internal',
      });
      if (firstBoneIndex === null) continue;

      if (chainBoneRefs.length === 1) {
        const chainColor = getChainThreeColor(chainName);
        const singleBoneIndex = skeleton.resolveBoneRef(chainBoneRefs[0], {
          namespace: 'internal',
        });
        if (singleBoneIndex === null) continue;
        const startPos = bonePositionsByIndex.get(singleBoneIndex);
        if (!startPos) continue;
        appendSingleChainMarkerDot(chainColor, startPos);
        nextChainMarkerHitTargets.push({
          key: `chain-marker-hit-${chainName}`,
          position: startPos.clone(),
          targetBoneIndex: firstBoneIndex,
        });
        continue;
      }

      if (chainBoneRefs.length < 2) continue;
      const chainColor = getChainThreeColor(chainName);
      const colorSegments = getSegmentBucket(chainColor);
      for (let i = 0; i < chainBoneRefs.length - 1; i += 1) {
        const startIndex = skeleton.resolveBoneRef(chainBoneRefs[i], {
          namespace: 'internal',
        });
        const endIndex = skeleton.resolveBoneRef(chainBoneRefs[i + 1], {
          namespace: 'internal',
        });
        if (startIndex === null || endIndex === null) continue;
        const startPos = bonePositionsByIndex.get(startIndex);
        const endPos = bonePositionsByIndex.get(endIndex);
        if (!startPos || !endPos) continue;
        colorSegments.push(
          startPos.x,
          startPos.y,
          startPos.z,
          endPos.x,
          endPos.y,
          endPos.z,
        );
        nextChainLineHitEdges.push({
          start: startPos.clone(),
          end: endPos.clone(),
          targetBoneIndex: firstBoneIndex,
        });
      }
    }

    const nextChainLineObjects = Array.from(segmentsByColor.entries())
      .filter(([, positions]) => positions.length > 0)
      .map(([color, positions], index) => {
        const geometry = new LineSegmentsGeometry();
        geometry.setPositions(positions);
        const material = new LineMaterial({
          color,
          linewidth: effectiveChainWidthPx,
          transparent: false,
          opacity: CHAIN_LINE_OPACITY,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        });
        const line = new LineSegments2(geometry, material);
        line.renderOrder = CHAIN_LINE_RENDER_ORDER;
        line.frustumCulled = false;
        return {
          key: `${color}-${index}`,
          geometry,
          material,
          line,
        };
      });

    const buildSingleDotObjects = (
      positionsByColor: Map<number, number[]>,
      sizePx: number,
      renderOrder: number,
      kind: 'marker',
    ) =>
      Array.from(positionsByColor.entries())
        .filter(([, positions]) => positions.length > 0)
        .map(([color, positions], index) => {
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3),
          );
          const material = new THREE.PointsMaterial({
            color,
            size: sizePx,
            sizeAttenuation: false,
            map: chainDotTexture ?? undefined,
            alphaTest: 0.25,
            transparent: false,
            opacity: CHAIN_LINE_OPACITY,
            depthTest: false,
            depthWrite: false,
            toneMapped: false,
          });
          const points = new THREE.Points(geometry, material);
          points.renderOrder = renderOrder;
          points.frustumCulled = false;
          return {
            key: `${kind}-${color}-${index}`,
            geometry,
            material,
            points,
          };
        });

    const nextChainSingleDotObjects = [
      ...buildSingleDotObjects(
        singleChainMarkerDotsByColor,
        effectiveChainMarkerSizePx,
        CHAIN_SINGLE_MARKER_RENDER_ORDER,
        'marker',
      ),
    ];

    return {
      chainLineObjects: nextChainLineObjects,
      chainSingleDotObjects: nextChainSingleDotObjects,
      chainLineHitEdges: nextChainLineHitEdges,
      chainMarkerHitTargets: nextChainMarkerHitTargets,
    };
  }, [
    bonePositionsByIndex,
    chainDotTexture,
    effectiveChainMarkerSizePx,
    effectiveChainWidthPx,
    mapping,
    boneAssignmentsViewerFocus,
    skeleton,
    showChains,
  ]);
  const chainLineHitTargets = useMemo(
    () => buildSegmentHitTargets(chainLineHitEdges, 'chain-line-hit'),
    [chainLineHitEdges],
  );

  useEffect(() => {
    for (const { material } of chainLineObjects) {
      material.resolution.set(size.width, size.height);
    }
  }, [chainLineObjects, size.height, size.width]);

  useEffect(
    () => () => {
      chainDotTexture?.dispose();
    },
    [chainDotTexture],
  );
  useEffect(
    () => () => {
      lineSegments.dispose();
      excludedLineSegments.dispose();
    },
    [excludedLineSegments, lineSegments],
  );
  useEffect(
    () => () => {
      jointGeo.dispose();
      selectionJointGeo.dispose();
    },
    [jointGeo, selectionJointGeo],
  );
  useEffect(
    () => () => {
      segmentPickGeo.dispose();
      markerPickGeo.dispose();
    },
    [markerPickGeo, segmentPickGeo],
  );

  useEffect(
    () => () => {
      for (const { geometry, material } of chainLineObjects) {
        geometry.dispose();
        material.dispose();
      }
      for (const { geometry, material } of chainSingleDotObjects) {
        geometry.dispose();
        material.dispose();
      }
    },
    [chainLineObjects, chainSingleDotObjects],
  );

  const shouldRenderChains =
    showChains &&
    (chainLineObjects.length > 0 || chainSingleDotObjects.length > 0);
  const shouldRenderRoles = visibleRoleTagsByBone.size > 0;
  const shouldRenderProperties = showProperties && propertyBoneIndices.size > 0;
  const selectionInteractive = typeof onSelectBone === 'function';
  const canShowViewerPickPreview =
    boneAssignmentsViewerPickMode !== null && selectionInteractive;
  const viewerPickPreviewColor =
    boneAssignmentsViewerPickMode === null
      ? null
      : boneAssignmentsViewerPickMode.kind === 'chain'
        ? getChainThreeColor(boneAssignmentsViewerPickMode.chainName)
        : ROLE_PICK_PREVIEW_COLOR;
  const propertiesBadgeInteractive =
    typeof onSelectBoneFromPropertiesBadge === 'function' || selectionInteractive;
  const setHoveredBonePreview = useCallback(
    (targetBoneIndex: number) => {
      if (!canShowViewerPickPreview) return;
      setHoveredBoneIndex((currentBoneIndex) =>
        currentBoneIndex === targetBoneIndex ? currentBoneIndex : targetBoneIndex,
      );
    },
    [canShowViewerPickPreview],
  );
  const clearHoveredBonePreview = useCallback(
    (targetBoneIndex?: number) => {
      setHoveredBoneIndex((currentBoneIndex) => {
        if (targetBoneIndex != null && currentBoneIndex !== targetBoneIndex) {
          return currentBoneIndex;
        }
        return null;
      });
    },
    [],
  );
  useEffect(() => {
    if (canShowViewerPickPreview) return;
    setHoveredBoneIndex(null);
  }, [canShowViewerPickPreview]);
  useEffect(() => {
    const handlePointerLeave = () => setHoveredBoneIndex(null);
    gl.domElement.addEventListener('pointerleave', handlePointerLeave);
    return () =>
      gl.domElement.removeEventListener('pointerleave', handlePointerLeave);
  }, [gl]);
  const handlePickPointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!selectionInteractive) return;
    if (selectionStopsPropagation) {
      event.stopPropagation();
    }
  };
  const hasChainPickIntersection = useCallback(
    (event: ThreeEvent<PointerEvent | MouseEvent>): boolean => {
      for (const intersection of event.intersections) {
        if (intersection.object.userData?.[CHAIN_PICK_USER_DATA_KEY] === true) {
          return true;
        }
      }
      return false;
    },
    [],
  );
  const handleBoneLinePickPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (hasChainPickIntersection(event)) return;
      handlePickPointerDown(event);
    },
    [hasChainPickIntersection],
  );
  const resolvePreferredBonePickIndex = useCallback(
    (event: ThreeEvent<PointerEvent | MouseEvent>): number | null => {
      for (const intersection of event.intersections) {
        const candidate = intersection.object.userData?.[
          BONE_PICK_INDEX_USER_DATA_KEY
        ];
        if (typeof candidate === 'number') {
          return candidate;
        }
      }
      return null;
    },
    [],
  );
  const createBoneLineHoverHandler =
    (targetBoneIndex: number) => (event: ThreeEvent<PointerEvent>) => {
      if (hasChainPickIntersection(event)) return;
      setHoveredBonePreview(targetBoneIndex);
    };
  const createSelectBoneClickHandler =
    (targetBoneIndex: number) => (event: ThreeEvent<MouseEvent>) => {
      if (!selectionInteractive) return;
      if (selectionStopsPropagation) {
        event.stopPropagation();
      }
      if (event.nativeEvent.button !== 0) return;
      onSelectBone?.(targetBoneIndex);
    };
  const createBoneLineSelectBoneClickHandler =
    (targetBoneIndex: number) => (event: ThreeEvent<MouseEvent>) => {
      if (!selectionInteractive) return;
      if (hasChainPickIntersection(event)) return;
      if (selectionStopsPropagation) {
        event.stopPropagation();
      }
      if (event.nativeEvent.button !== 0) return;
      onSelectBone?.(targetBoneIndex);
    };
  const createChainHoverHandler =
    (targetBoneIndex: number) => (event: ThreeEvent<PointerEvent>) => {
      const preferredBoneIndex = resolvePreferredBonePickIndex(event);
      setHoveredBonePreview(preferredBoneIndex ?? targetBoneIndex);
    };
  const createChainSelectBoneClickHandler =
    (targetBoneIndex: number) => (event: ThreeEvent<MouseEvent>) => {
      if (!selectionInteractive) return;
      if (selectionStopsPropagation) {
        event.stopPropagation();
      }
      if (event.nativeEvent.button !== 0) return;
      const preferredBoneIndex = resolvePreferredBonePickIndex(event);
      onSelectBone?.(preferredBoneIndex ?? targetBoneIndex);
    };
  const selectedJointData =
    selectedBoneIndex == null
      ? null
      : jointData.find(({ idx }) => idx === selectedBoneIndex) ?? null;
  const hoveredJointData =
    hoveredBoneIndex == null
      ? null
      : jointData.find(({ idx }) => idx === hoveredBoneIndex) ?? null;
  useEffect(() => {
    dispatchViewerBoneHoverName(
      canShowViewerPickPreview && hoveredJointData
        ? skeleton.bone(hoveredJointData.idx).name
        : null,
    );
  }, [canShowViewerPickPreview, hoveredJointData, skeleton]);
  useEffect(
    () => () => {
      dispatchViewerBoneHoverName(null);
    },
    [],
  );
  const handleSelectedHighlightClick = (event: ThreeEvent<MouseEvent>) => {
    if (!selectionInteractive) return;
    if (selectionStopsPropagation) {
      event.stopPropagation();
    }
    if (event.nativeEvent.button !== 0) return;
    if (!selectedJointData) return;
    onSelectBone?.(selectedJointData.idx);
  };
  if (
    !showBones &&
    !shouldRenderChains &&
    !shouldRenderRoles &&
    !shouldRenderProperties &&
    selectedJointData == null &&
    hoveredJointData == null
  ) {
    return null;
  }

  return (
    <group renderOrder={999}>
      {shouldRenderRoles &&
        jointData.map(({ pos, idx }) => {
          const roleTags = visibleRoleTagsByBone.get(idx);
          if (!roleTags || roleTags.length === 0) return null;
          const handleRoleTagPointerDown = (
            event: React.PointerEvent<HTMLDivElement>,
          ) => {
            if (!selectionInteractive) return;
            if (selectionStopsPropagation) {
              event.stopPropagation();
            }
          };
          const handleRoleTagClick = (event: React.MouseEvent<HTMLDivElement>) => {
            if (!selectionInteractive) return;
            if (selectionStopsPropagation) {
              event.stopPropagation();
            }
            if (event.button !== 0) return;
            onSelectBone?.(idx);
          };

          return (
            <Html
              key={`role-tag-${idx}`}
              position={pos}
              center
              zIndexRange={HTML_OVERLAY_Z_INDEX_RANGE}
              style={{ pointerEvents: 'auto' }}
            >
              <div
                className="flex max-w-[220px] cursor-pointer flex-col items-start gap-0.5"
                style={{ transform: 'translate(14px, -12px)' }}
                onPointerDown={handleRoleTagPointerDown}
                onClick={handleRoleTagClick}
              >
                {roleTags.map(({ role, backgroundColor, isUserAssigned }) => (
                  <div
                    key={`role-tag-${idx}-${role}`}
                    className={`max-w-full rounded-full px-2 py-0.5 ${
                      isUserAssigned ? 'border border-black/10' : ''
                    }`}
                    style={{ backgroundColor }}
                  >
                    <Text
                      variant="studio/body/medium/xs"
                      className={`block truncate ${
                        isUserAssigned ? 'text-black' : 'text-white'
                      }`}
                    >
                      {role}
                    </Text>
                  </div>
                ))}
              </div>
            </Html>
          );
        })}
      {shouldRenderProperties &&
        jointData.map(({ pos, idx }) => {
          if (!propertyBoneIndices.has(idx)) return null;
          const propertyTooltipLabels =
            propertyBadgeTooltipByBone.get(idx) ?? ['Bone has properties'];
          const propertyTooltipText =
            propertyTooltipLabels.length <= 1 ? (
              propertyTooltipLabels[0]
            ) : (
              <span className="flex flex-col items-start gap-0.5">
                {propertyTooltipLabels.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </span>
            );
          const handlePropertyBadgePointerDown = (
            event: React.PointerEvent<HTMLDivElement>,
          ) => {
            if (!propertiesBadgeInteractive) return;
            event.stopPropagation();
          };
          const handlePropertyBadgePointerUp = (
            event: React.PointerEvent<HTMLDivElement>,
          ) => {
            if (!propertiesBadgeInteractive) return;
            event.stopPropagation();
          };
          const handlePropertyBadgeClick = (
            event: React.MouseEvent<HTMLDivElement>,
          ) => {
            if (!propertiesBadgeInteractive) return;
            event.stopPropagation();
            if (event.button !== 0) return;
            if (onSelectBoneFromPropertiesBadge) {
              onSelectBoneFromPropertiesBadge(idx);
              return;
            }
            onSelectBone?.(idx);
          };

          return (
            <Html
              key={`property-badge-${idx}`}
              position={pos}
              center
              zIndexRange={HTML_OVERLAY_Z_INDEX_RANGE}
              style={{ pointerEvents: 'none' }}
            >
              <div
                className={`${propertiesBadgeInteractive ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'} inline-flex`}
                style={{ transform: 'translate(14px, 8px)' }}
                onPointerDown={handlePropertyBadgePointerDown}
                onPointerUp={handlePropertyBadgePointerUp}
                onClick={handlePropertyBadgeClick}
              >
                <BonePropertiesBadge
                  tooltipText={propertyTooltipText}
                  tooltipAriaLabel={propertyTooltipLabels.join(', ')}
                  tooltipDisabled={false}
                />
              </div>
            </Html>
          );
        })}
      {viewerPickPreviewColor !== null && hoveredJointData && (
        <mesh
          position={hoveredJointData.pos}
          geometry={selectionJointGeo}
          renderOrder={1001}
          scale={BONE_PICK_PREVIEW_SCALE_MULTIPLIER}
          raycast={() => undefined}
        >
          <meshBasicMaterial
            color={viewerPickPreviewColor}
            depthTest={false}
            depthWrite={false}
            transparent
            opacity={BONE_PICK_PREVIEW_OPACITY}
            toneMapped={false}
          />
        </mesh>
      )}
      {hoveredChainColor !== null &&
        hoveredChainEndpoints.map(({ key, position }) => (
          <mesh
            key={`hovered-chain-highlight-${key}`}
            position={position}
            geometry={selectionJointGeo}
            renderOrder={1001}
            scale={CHAIN_ENDPOINT_HIGHLIGHT_SCALE_MULTIPLIER}
          >
            <meshBasicMaterial
              color={hoveredChainColor}
              depthTest={false}
              depthWrite={false}
              transparent
              opacity={0.34}
              toneMapped={false}
            />
          </mesh>
        ))}
      {hoveredChainColorCss &&
        hoveredChainEndpoints.map(({ key, position, endpointLabel, boneName }) => (
          <Html
            key={`hovered-chain-label-${key}`}
            position={position}
            center
            zIndexRange={HTML_OVERLAY_Z_INDEX_RANGE}
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="max-w-[220px]"
              style={{ transform: getHoveredChainLabelTransform(endpointLabel) }}
            >
              <div
                className="rounded-full border border-white/50 px-2 py-0.5 shadow-sm"
                style={{ backgroundColor: hoveredChainColorCss }}
              >
                <Text
                  as="span"
                  variant="studio/body/medium/xs"
                  className="block truncate text-white"
                >
                  {`${endpointLabel}: ${boneName}`}
                </Text>
              </div>
            </div>
          </Html>
        ))}
      {showBones && (
        <lineSegments geometry={lineSegments} renderOrder={BONE_LINE_RENDER_ORDER}>
          <lineBasicMaterial
            color={baseBoneColor}
            linewidth={BONE_STROKE_WIDTH}
            depthTest={false}
            transparent={false}
          />
        </lineSegments>
      )}
      {showBones && hasExcludedParentSegments && (
        <lineSegments
          geometry={excludedLineSegments}
          renderOrder={BONE_LINE_RENDER_ORDER}
        >
          <lineBasicMaterial
            color={EXCLUDED_BONE_COLOR}
            linewidth={BONE_STROKE_WIDTH}
            transparent={false}
            depthTest={false}
          />
        </lineSegments>
      )}
      {showBones &&
        boneLineHitTargets.map(
          ({ key, midpoint, quaternion, length, targetBoneIndex }) => (
            <mesh
              key={key}
              position={midpoint}
              quaternion={quaternion}
              scale={[boneLinePickRadius, length, boneLinePickRadius]}
              geometry={segmentPickGeo}
              onPointerDown={handleBoneLinePickPointerDown}
              onPointerOver={createBoneLineHoverHandler(targetBoneIndex)}
              onPointerMove={createBoneLineHoverHandler(targetBoneIndex)}
              onPointerOut={() => clearHoveredBonePreview(targetBoneIndex)}
              onClick={createBoneLineSelectBoneClickHandler(targetBoneIndex)}
            >
              <meshBasicMaterial
                transparent
                opacity={0}
                depthTest={false}
                depthWrite={false}
                colorWrite={false}
              />
            </mesh>
          ),
        )}
      {shouldRenderChains &&
        chainLineObjects.map(({ key, line }) => (
          <primitive key={key} object={line} raycast={() => undefined} />
        ))}
      {shouldRenderChains &&
        chainSingleDotObjects.map(({ key, points }) => (
          <primitive
            key={`single-${key}`}
            object={points}
            raycast={() => undefined}
          />
        ))}
      {shouldRenderChains &&
        chainLineHitTargets.map(
          ({ key, midpoint, quaternion, length, targetBoneIndex }) => (
            <mesh
              key={key}
              position={midpoint}
              quaternion={quaternion}
              scale={[chainLinePickRadius, length, chainLinePickRadius]}
              geometry={segmentPickGeo}
              userData={{ [CHAIN_PICK_USER_DATA_KEY]: true }}
              onPointerDown={handlePickPointerDown}
              onPointerOver={createChainHoverHandler(targetBoneIndex)}
              onPointerMove={createChainHoverHandler(targetBoneIndex)}
              onPointerOut={() => clearHoveredBonePreview(targetBoneIndex)}
              onClick={createChainSelectBoneClickHandler(targetBoneIndex)}
            >
              <meshBasicMaterial
                transparent
                opacity={0}
                depthTest={false}
                depthWrite={false}
                colorWrite={false}
              />
            </mesh>
          ),
        )}
      {shouldRenderChains &&
        chainMarkerHitTargets.map(({ key, position, targetBoneIndex }) => (
          <mesh
            key={key}
            position={position}
            scale={[
              chainMarkerPickRadius,
              chainMarkerPickRadius,
              chainMarkerPickRadius,
            ]}
            geometry={markerPickGeo}
            userData={{ [CHAIN_PICK_USER_DATA_KEY]: true }}
            onPointerDown={handlePickPointerDown}
            onPointerOver={createChainHoverHandler(targetBoneIndex)}
            onPointerMove={createChainHoverHandler(targetBoneIndex)}
            onPointerOut={() => clearHoveredBonePreview(targetBoneIndex)}
            onClick={createChainSelectBoneClickHandler(targetBoneIndex)}
          >
            <meshBasicMaterial
              transparent
              opacity={0}
              depthTest={false}
              depthWrite={false}
              colorWrite={false}
            />
          </mesh>
        ))}
      {!showBones && showSelectionHighlight && selectedJointData && (
        <SelectionHalo
          position={selectedJointData.pos}
          geometry={selectionJointGeo}
          bonePickIndex={selectedJointData.idx}
          onPointerDown={handlePickPointerDown}
          onPointerOver={() => setHoveredBonePreview(selectedJointData.idx)}
          onPointerMove={() => setHoveredBonePreview(selectedJointData.idx)}
          onPointerOut={() => clearHoveredBonePreview(selectedJointData.idx)}
          onClick={handleSelectedHighlightClick}
        />
      )}
      {showBones &&
        jointData.map(({ pos, color, idx, isExcluded }) => {
          const isSelected = idx === selectedBoneIndex;
          const jointColor = focusedChainEndpointColorByBone.get(idx) ?? color;
          const handleJointPointerDown = (event: ThreeEvent<PointerEvent>) => {
            if (!selectionInteractive) return;
            if (selectionStopsPropagation) {
              event.stopPropagation();
            }
          };
          const handleJointClick = (event: ThreeEvent<MouseEvent>) => {
            if (!selectionInteractive) return;
            if (selectionStopsPropagation) {
              event.stopPropagation();
            }
            if (event.nativeEvent.button !== 0) return;
            onSelectBone?.(idx);
          };
          return (
            <group key={idx}>
              {showSelectionHighlight && isSelected && (
                <SelectionHalo
                  position={pos}
                  geometry={selectionJointGeo}
                  bonePickIndex={idx}
                  onPointerDown={handleJointPointerDown}
                  onPointerOver={() => setHoveredBonePreview(idx)}
                  onPointerMove={() => setHoveredBonePreview(idx)}
                  onPointerOut={() => clearHoveredBonePreview(idx)}
                  onClick={handleJointClick}
                />
              )}
              <mesh
                position={pos}
                geometry={jointGeo}
                renderOrder={1000}
                userData={{ [BONE_PICK_INDEX_USER_DATA_KEY]: idx }}
                onPointerDown={handleJointPointerDown}
                onPointerOver={() => setHoveredBonePreview(idx)}
                onPointerMove={() => setHoveredBonePreview(idx)}
                onPointerOut={() => clearHoveredBonePreview(idx)}
                onClick={handleJointClick}
              >
                <meshBasicMaterial
                  color={jointColor}
                  depthTest={false}
                  transparent={false}
                  colorWrite={!isExcluded}
                  depthWrite={!isExcluded}
                />
              </mesh>
              {isExcluded && <ExcludedBoneRing position={pos} radius={jointRadius} />}
              {showBoneNames && (
                <Html
                  position={pos}
                  center
                  zIndexRange={HTML_OVERLAY_Z_INDEX_RANGE}
                  style={{ pointerEvents: 'none' }}
                >
                  <div className="whitespace-nowrap rounded bg-black/60 px-1 py-0.5 text-white">
                    <Text
                      as="span"
                      variant="studio/body/medium/xs"
                      className="text-white"
                    >
                      {skeleton.bone(idx).name}
                    </Text>
                  </div>
                </Html>
              )}
            </group>
          );
        })}
    </group>
  );
}

function getHoveredChainLabelTransform(
  endpointLabel: HoveredChainEndpoint['endpointLabel'],
): string {
  if (endpointLabel === 'End') {
    return 'translate(14px, 10px)';
  }
  if (endpointLabel === 'Start / End') {
    return 'translate(14px, -4px)';
  }
  return 'translate(14px, -18px)';
}
